import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Modal, Spin, Collapse, Tooltip } from 'antd'
import { IoArrowBack } from 'react-icons/io5'
import { Document, Page, pdfjs } from 'react-pdf'
import styled from 'styled-components'
import { useIntersectionObserver } from '@wojtekmaj/react-hooks'
import { CaretRightOutlined } from '@ant-design/icons';
import { GoArrowUpRight } from "react-icons/go";
import { MdDragIndicator } from "react-icons/md";
import { DOCUMENT_TYPE } from '../utils/type' 
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const observerConfig = {
  threshold: 0.3,
}
const { Panel } = Collapse;

function formatScore ( num , digits = 10 ) {
  if (num === 0) return 0;
  return Number(num).toFixed(digits).replace(/\.?0+$/, '');
}

function formatScore2 (input , digitsHead = 3 ) {
  const num = Number(input);
  if (!num) return '0';

  const [ headRaw , tailRaw ] = num.toExponential().split('e');
  const tail = tailRaw.replace('+', '');
 
  const head = Number(headRaw).toPrecision(digitsHead);
  
  const superscriptMap = {
    '0': '⁰',
    '1': '¹',
    '2': '²',
    '3': '³',
    '4': '⁴',
    '5': '⁵',
    '6': '⁶',
    '7': '⁷',
    '8': '⁸',
    '9': '⁹',
    '-': '⁻',
  };

  const formattedTail = tail
    .split('')
    .map((char) => superscriptMap[char])
    .join('');

  return `${head} x 10${formattedTail}`;
}

function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

export default function PreviewPDFBase64({ base64Data, fileName = 'Document.pdf', isVisible, setIsVisible, data }) {
  const [numPages, setNumPages] = useState(null)
  const [visiblePages, setVisiblePages] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [scale, setScale] = useState(1)
  const [pdfDataUri, setPdfDataUri] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeKeys, setActiveKeys] = useState([]);

  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const pageRefs = useRef({})
  const menuRef = useRef(null);

  const panelRefs = useRef({});
  const scrollToPanel = useCallback((panelKey) => {
    const panelElement = panelRefs.current[panelKey];
    if (panelElement && menuRef.current) {
        const menuElement = menuRef.current;
        const panelTop = panelElement.offsetTop;
        const menuScrollTop = menuElement.scrollTop;
        const menuHeight = menuElement.clientHeight;
        const panelHeight = panelElement.clientHeight;
        
        const targetScroll = panelTop - (menuHeight / 2) + (panelHeight / 2);
        
        menuElement.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth'
        });
    }
    }, []);

  const size = useWindowSize()
  const isMobile = size.width <= 739

  const minWidth = isMobile ? size.width : (size.width * 1) / 2
  const maxWidth = isMobile ? size.width : minWidth + 1000

    useEffect(() => {
    if (activeKeys.length > 0) {
        setTimeout(() => {
        scrollToPanel(activeKeys[activeKeys.length - 1]);
        }, 100);
    }
    }, [activeKeys, scrollToPanel]);

  useEffect(() => {
    if (size.width <= 739) {
    setWidth(size.width)
    } else {
    setWidth((size.width * 2) / 3)
    }
    }, [size.width])

  const [width, setWidth] = useState(minWidth)

  // Reset 
  useEffect(() => {
    if (!isVisible) {
      setNumPages(null)
      setVisiblePages({})
      setCurrentPage(1)
      setWidth(minWidth)
      setScale(1)
      setPdfDataUri(null)
      setLoading(true)
      setError(null)
      pageRefs.current = {}
      setMenuPosition({ x: 0, y: 100 })
    }
  }, [isVisible, minWidth])

  // Tính toán vị trí mặc định của menu dựa trên width
  useEffect(() => {
    if (isVisible) {
      const defaultX = (size.width / 4) - 420;
      setMenuPosition({ x: defaultX, y: 100 });
    }
  }, [isVisible, size.width])

  // Xử lý drag 
  const handleMouseDown = (e) => {
    if (e.target.closest('.drag-handle')) {
      setIsDragging(true);
      const menuElement = menuRef.current;
      if (menuElement) {
        const rect = menuElement.getBoundingClientRect();
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && menuRef.current) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Giới hạn vị trí trong viewport
      const menuWidth = 400;
      const menuHeight = menuRef.current.offsetHeight;
      const maxX = size.width - menuWidth;
      const maxY = size.height - menuHeight;
      
      setMenuPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  }, [isDragging, dragOffset, size]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  // Xử lý base64 data khi modal mở
  useEffect(() => {
    if (base64Data && isVisible) {
      try {
        setLoading(true)
        setError(null)

        let dataUri = base64Data

        if (!base64Data.startsWith('data:')) {
          const cleanBase64 = base64Data.replace(/\s/g, '')
          dataUri = `data:application/pdf;base64,${cleanBase64}`
        }

        setPdfDataUri(dataUri)
        setLoading(false)
      } catch (err) {
        console.error('Error processing base64 data:', err)
        setError('Không thể xử lý dữ liệu PDF')
        setLoading(false)
      }
    }
  }, [base64Data, isVisible])

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages)
    setLoading(false)
  }

  function onDocumentLoadError(error) {
    console.error('Error loading PDF:', error)
    setError('Không thể tải PDF. Vui lòng kiểm tra lại dữ liệu.')
    setLoading(false)
  }

  const setPageVisibility = useCallback((pageNumber, isIntersecting) => {
    setVisiblePages(prevVisiblePages => {
      const newVisiblePages = {
        ...prevVisiblePages,
        [pageNumber]: isIntersecting,
      }
      const values_true = Object.entries(newVisiblePages).filter(([key, value]) => value)
      if (values_true.length === 0) {
        return {
          ...prevVisiblePages,
        }
      }
      if (isIntersecting) {
        setCurrentPage(pageNumber)
        return {
          ...newVisiblePages,
          [pageNumber - 1]: false,
          [pageNumber + 1]: false,
        }
      }
      return newVisiblePages
    })
  }, [])

  const scrollToPage = useCallback((pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= numPages) {
      const pageElement = pageRefs.current[pageNumber]
      if (pageElement) {
        pageElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        })
        setCurrentPage(pageNumber)
      }
    }
  }, [numPages])

  const handleClose = () => {
    setIsVisible(false)
  }

  const handleChoosePage = (e, id) => {
    e.stopPropagation();
    // if(!activeKeys.includes(id.toString())) {
        scrollToPage(id+1)
    // } 
  }

  const checkTypeDauMoc = (numType) => {
    if (numType === "0") {
      return 'Mộc tròn màu đỏ'
    } else if (numType === "1") {
      return 'Mộc tròn màu đen'
    } else if (numType === "2") {
      return 'Mộc vuông màu đỏ'
    } else if (numType === "3") {
      return 'Mộc vuông màu xanh'
    } else if (numType === "4") {
      return 'Chữ ký điện tử'
    }
  }

  useEffect(()=> {
    setActiveKeys([(currentPage-1).toString()])
  },[currentPage])

  const shouldRenderUI = isVisible && pdfDataUri && !loading && !error

  return (
    <PreviewStyle width={size.width} height={size.height}>
      {shouldRenderUI && numPages && (
        <div className='body'>
          <div className='top' onClick={handleClose}>
            <IoArrowBack style={{ color: '#fff', fontSize: 25 }} />
          </div>
          <div className='bottom'>
            <div className='page-ctn'>
              <p>
                {'Trang: '}
                {currentPage} / {numPages}
              </p>
            </div>
          </div>

          <MenuCustome 
            ref={menuRef}
            style={{
              left: `${menuPosition.x}px`,
              top: `${menuPosition.y}px`,
              cursor: isDragging ? 'grabbing' : 'default'
            }}
            onMouseDown={handleMouseDown}
          >
            <div className='header-menu'>
                <div className='drag-handle'>
                  <MdDragIndicator style={{ fontSize: 20, color: '#667085', cursor: 'grab' }} />
                </div>
                <span>Thông tin giấy tờ</span>
            </div>
            <Collapse
              bordered={false}
              defaultActiveKey={['0']}
              expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
              className='collapse'
              onChange={(keys) => setActiveKeys(keys)}
              activeKey={activeKeys}
            >
                {data.map((item, index) => (
                  <Panel 
                    header={`Trang ${index + 1}`} 
                    key={item.id}
                    extra={
                        <Tooltip title="Đi tới trang">
                            <div 
                            style={{
                                padding: '4px 5px',
                                borderRadius: 12,
                                cursor: 'pointer',
                                lineHeight: 1,
                                display: 'block'
                            }}
                            onClick={(e) => handleChoosePage(e ,index)}
                            >
                                <GoArrowUpRight style={{fontSize: 18}}/>
                            </div>
                        </Tooltip>
                    }
                    >
                    <div 
                    ref={(el) => {
                        if (el) panelRefs.current[item.id] = el.closest('.ant-collapse-item');
                    }}
                    >
                        <PanelWrapper>
                            <PanelRow>
                                <PanelTitle>
                                    Tiêu đề:
                                </PanelTitle>
                                <PanelInfo>
                                    {item?.title?.length > 0 ? item?.title?.join(', ') : 'Không có'}
                                </PanelInfo>
                            </PanelRow>
                            <PanelRow>
                                <PanelTitle>
                                    Độ mờ:
                                </PanelTitle>
                                <PanelInfo>
                                    {formatScore(item?.input_quality?.blur?.score)} ({item?.input_quality?.blur?.value === false ? 'Ảnh rõ' : 'Ảnh mờ'})
                                </PanelInfo>
                            </PanelRow>
                            <PanelRow>
                                <PanelTitle>
                                    Loại giấy tờ:
                                </PanelTitle>
                                <PanelInfo>
                                    {item?.label ? `${DOCUMENT_TYPE[item?.label]?.label}` : 'Không xác định'  } 
                                </PanelInfo>
                            </PanelRow>
                            <PanelRow>
                                <PanelTitle>
                                    Loại ảnh:
                                </PanelTitle>
                                <PanelInfo>
                                    {item?.input_quality?.photocopy === true ? 'Ảnh photocopy' : 'Ảnh thường'} 
                                </PanelInfo>
                            </PanelRow>
                            <PanelRow>
                                <PanelTitle>
                                    Nhiều giấy tờ:
                                </PanelTitle>
                                <PanelInfo>
                                    {item?.input_quality?.multi_page === true ? 'Có' : 'Không'} 
                                </PanelInfo>
                            </PanelRow>
                            <PanelRow>
                                <PanelTitle>
                                    Con dấu:
                                </PanelTitle>
                                <PanelInfo>
                                    {item?.input_quality?.stamp?.length === 0 
                                        ? 'Không có' 
                                        : item?.input_quality?.stamp?.map(stamp => checkTypeDauMoc(stamp?.value)).join(', ')
                                    } 
                                </PanelInfo>
                            </PanelRow>
                        </PanelWrapper>
                    </div>
                  </Panel>
                ))}
            </Collapse>

          </MenuCustome>
        </div>
      )}
      <ModalStyle
        title={false}
        open={isVisible}
        onCancel={handleClose}
        footer={false}
        width={width * scale}
        closable={false}
        destroyOnClose={true}
      >
        {loading && (
          <div
            style={{
              width: '100%',
              height: size.height,
              backgroundColor: '#fff',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spin tip="Đang tải PDF..." />
          </div>
        )}

        {error && (
          <div
            style={{
              width: '100%',
              height: size.height,
              backgroundColor: '#fff',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <p style={{ color: 'red', fontSize: 16 }}>{error}</p>
          </div>
        )}

        {shouldRenderUI && (
          <Document
            file={pdfDataUri}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={() => (
              <div
                style={{
                  width: '100%',
                  height: size.height,
                  backgroundColor: '#fff',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Spin tip="Đang mở ..." />
              </div>
            )}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <PageWithObserver
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                setPageVisibility={setPageVisibility}
                width={width}
                scale={scale}
                pageRefs={pageRefs}
              />
            ))}
          </Document>
        )}
      </ModalStyle>
    </PreviewStyle>
  )
}

function PageWithObserver({ pageNumber, setPageVisibility, scale, pageRefs, ...otherProps }) {
  const [page, setPage] = useState()
  const containerRef = useRef()
  
  const onIntersectionChange = useCallback(
    ([entry]) => {
      setPageVisibility(pageNumber, entry.isIntersecting)
    },
    [pageNumber, setPageVisibility]
  )

  useIntersectionObserver(page, observerConfig, onIntersectionChange)

  useEffect(() => {
    if (containerRef.current) {
      pageRefs.current[pageNumber] = containerRef.current
    }
  }, [pageNumber, pageRefs])

  return (
    <div ref={containerRef}>
      <Page canvasRef={setPage} pageNumber={pageNumber} {...otherProps} scale={scale} loading={false} />
      <div className='separator' />
    </div>
  )
}

const ModalStyle = styled(Modal)`
  .ant-modal-wrap {
    .ant-modal {
      width: ${props => props.width}px;
      margin-left: 100px;
    }
  }
  .ant-modal-header {
    width: ${props => props.width}px;
    background-color: #fff;
    padding: 16px 16px 0px 16px;
  }
  .ant-modal-content {
    background-color: transparent;
  }
  .ant-modal-body {
    width: ${props => props.width}px;
    background-color: rgba(0, 0, 0, 0.1);
    padding: 0px;
  }
  .button-container {
    display: flex;
    flex-direction: row;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.12);
    border-radius: 4px;
  }
  .separator {
    height: 20px;
    width: 100%;
  }
  .react-pdf__Page {
    background-color: #fff;
  }

`

const PreviewStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  .body {
    position: fixed;
    z-index: 1003;
    width: ${props => props.width}px;
    left: 0px;
  }
  .top {
    position: fixed;
    display: flex;
    align-items: center;
    top: 35px;
    left: 35px;
    cursor: pointer;
    p {
      color: #fff;
      font-size: 16px;
      margin: 0;
    }
  }
  .bottom {
    position: fixed;
    display: flex;
    justify-content: center;
    bottom: 30px;
    width: ${props => props.width}px;
    .page-ctn {
      background-color: rgba(0, 0, 0, 0.93);
      padding: 10px 12px;
      display: flex;
      border-radius: 4px;
      align-items: center;

      p {
        font-size: 14px;
        color: #fff;
        margin: 0;
      }
      .icon {
        font-size: 20px;
      }
      .nav-icon {
        font-size: 24px;
        transition: opacity 0.2s;
        &:hover:not([style*="cursor: auto"]) {
          opacity: 0.7;
        }
      }
      .icon-zoom {
        color: #fff;
        font-size: 17px;
        margin-right: 15px;
        margin-left: 15px;
        cursor: pointer;
        transition: opacity 0.2s;
        &:hover {
          opacity: 0.7;
        }
      }
      .page-input-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 12px;
        
        .page-input {
          width: 50px;
          background-color: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: #fff;
          text-align: center;
          
          .ant-input-number-input {
            color: #fff;
            text-align: center;
          }
          
          &:hover, &:focus {
            background-color: rgba(255, 255, 255, 0.3);
            border-color: rgba(255, 255, 255, 0.5);
          }
        }
        
        .page-total {
          color: #fff;
          font-size: 12px;
          white-space: nowrap;
        }
      }
    }
  }

`

const MenuCustome = styled.div`
    width: 400px;
    max-height: calc(100vh - 200px);
    overflow-y: scroll;
    background-color: #FFF;
    padding: 20px 12px 20px 20px;
    border-radius: 12px;
    position: fixed;
    display: flex;
    flex-direction: column;
    border: 1px solid #e5e7eb;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
    user-select: none;
    transition: box-shadow 0.2s;
    
    &:active .drag-handle {
      cursor: grabbing;
    }
    
    .header-menu { 
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 10px;
        font-size: 18px;
        font-weight: 600;
        color: #101828;
        position: relative;
        gap: 8px;
        
        .drag-handle {
          display: flex;
          align-items: center;
          position: absolute;
          left: 0;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
          
          &:hover {
            background-color: #f3f4f6;
          }
          
          &:active {
            cursor: grabbing;
          }
        }
    }

    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #fff;
        border-radius: 0 12px 12px 0;
    }

    &::-webkit-scrollbar-thumb {
        background: #98a2b3;
        border-radius: 0 12px 12px 0;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #667085;
    }

    @media screen and (max-width: 739px) {
        display: none;
    }
`
const PanelWrapper = styled.div`

`

const PanelRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`
const PanelTitle = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: #475467;
`

const PanelInfo = styled.div`
    max-width: 200px;
    font-size: 14px;
    font-weight: 600;
    color: #101828;
`